// ---------------------------------------------------------------------------
// Connection + per-project sync state for Google Drive sync — a plain
// module-level singleton (same pattern as aemetJobStore.ts), subscribable
// via useSyncExternalStore. Kept deliberately separate from any open
// project's state: unlike AEMET (tied to a sub-basin of the current
// project), Drive connects once per browser, independent of what's open.
//
// `queueSync`/`deleteRemote` are called from useProjectPersistence.ts right
// after every real local `saveProject`/`deleteSavedProject` — IndexedDB is
// always written first and is never blocked on Drive; sync is
// fire-and-forget from the caller's point of view. Reusing exportProject.ts's
// own `.ehdz` blob as the Drive file content: the same format already used
// for "Export copy" is what gets uploaded, no separate serialization.
//
// `reconcile` runs once, right after a successful connect/silent-reconnect
// (see useProjectPersistence.ts's startup effect): matches the app's Drive
// folder against the local project list by `appProperties.evalhidProjectId`
// (not file name, which can drift after a local rename) — a project that
// only exists in Drive gets pulled down and saved locally under the SAME id
// (unlike "Import copy", which deliberately mints a new one: reconcile is
// re-attaching an already-synced project, not importing an independent
// copy); a project that only exists locally is left alone and reported back
// in `unsyncedLocal` for the caller to ask the user about (never uploaded
// silently); a project in both with Drive meaningfully newer is flagged
// `"conflict"` instead of silently overwritten either way; a project in
// both with the LOCAL copy meaningfully newer (edits made while
// disconnected/offline, so `queueSync`'s own real-time upload never ran for
// them — see its `!this.accessToken` no-op) gets pushed to Drive right here
// instead of just being marked "synced" without ever having actually
// uploaded the newer content.
// ---------------------------------------------------------------------------
import { exportProject, importProject } from "./exportProject.ts";
import { sanitizedProjectFileName } from "./fileNaming.ts";
import { TranslatableError } from "../i18n/translate.ts";
import {
  deleteProjectFile,
  downloadProjectFile,
  findOrCreateAppFolder,
  listProjectFiles,
  requestAccessToken,
  revokeAccessToken,
  uploadProjectFile,
} from "./googleDrive.ts";
import {
  loadSavedProject,
  saveProject,
  buildProjectMeta,
  type ProjectData,
  type ProjectMeta,
} from "./storage.ts";

const CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined)?.trim() || null;
const CONNECTION_STORAGE_KEY = "evalhid-drive-connected";
/**
 * Refuse to reuse a cached token this close to expiring — better to go
 * through a real reconnect (silent if it can be) than risk a Drive API call
 * racing past the real expiry mid-request.
 */
const EXPIRY_SAFETY_MARGIN_MS = 2 * 60 * 1000;

/**
 * The access token itself is cached here (not just "was connected before")
 * so a later page load can reuse it directly — see `tryReuseStoredToken` —
 * without ever going through Google again until it actually expires (Drive
 * tokens last about an hour). `sessionStorage`, not `localStorage` (changed
 * 2026-08-25, security review): cleared on tab close and never shared across
 * tabs, cutting the exposure window down to "this tab, this session" instead
 * of "until the token expires, from any tab, even after the browser
 * restarts" — the reuse this exists for (skip Google on every reload within
 * the same work session) still works exactly the same, only a genuinely new
 * tab/session goes through `trySilentReconnect` instead of an instant reuse.
 * Also still narrow in scope regardless: the `drive.file` scope this app
 * requests can only ever see files/folders the app itself created, and the
 * token is useless again within the hour either way.
 */
interface StoredConnection {
  accessToken: string;
  folderId: string;
  /** Epoch ms — see googleDrive.ts's `AccessTokenResult`. */
  expiresAt: number;
}

function readStoredConnection(): StoredConnection | null {
  try {
    const raw = sessionStorage.getItem(CONNECTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredConnection>;
    if (
      typeof parsed.accessToken === "string" &&
      typeof parsed.folderId === "string" &&
      typeof parsed.expiresAt === "number"
    ) {
      return parsed as StoredConnection;
    }
    return null;
  } catch {
    return null;
  }
}

function saveStoredConnection(connection: StoredConnection | null): void {
  try {
    if (connection) sessionStorage.setItem(CONNECTION_STORAGE_KEY, JSON.stringify(connection));
    else sessionStorage.removeItem(CONNECTION_STORAGE_KEY);
  } catch {
    // sessionStorage unavailable: just won't be able to reuse or reconnect next time.
  }
}

interface DriveSyncSnapshot {
  connected: boolean;
  connecting: boolean;
  /** Raw thrown value from the last failed connect attempt — resolve with `translateError(lastError, t)` at the display site, same idiom as the rest of the app (see translate.ts). `null` once a connect succeeds or on a fresh page load. */
  lastError: unknown;
  /**
   * True once a reconnect attempt (silent or manual) has settled without
   * ending up connected, but there's still a stored connection from an
   * earlier session — i.e. Drive USED to work here and now doesn't, not
   * just "never set up". TopBar.tsx's Settings button uses this for a
   * small orange dot (same `.badge` as the editor/notices icons) — a
   * silent reconnect failure is deliberately invisible everywhere else
   * (see `surfaceErrors`), so without this, the ONLY way to notice Drive
   * had quietly stopped syncing would be spotting a ⚠ on some individual
   * saved project, which is easy to miss entirely.
   */
  needsReconnect: boolean;
}

type ProjectSyncStatus = "not-synced" | "syncing" | "synced" | "error" | "conflict";

/** Local vs. Drive `lastModified` considered "the same moment" within this margin — avoids flagging a false conflict from clock skew/rounding when nothing really changed. */
const CONFLICT_THRESHOLD_MS = 2000;

interface ProjectSyncState {
  status: ProjectSyncStatus;
  driveFileId: string | null;
  lastSyncedAt: number | null;
  lastError: unknown;
}

const NOT_SYNCED: ProjectSyncState = {
  status: "not-synced",
  driveFileId: null,
  lastSyncedAt: null,
  lastError: null,
};

/**
 * The Drive file name for a project — unlike `exportProject.ts`'s own
 * `exportedFileName` (used for the local, one-off "Export copy" download,
 * where the exact moment of the download already gives the user context
 * for what it is), this always appends a short, stable fragment of the
 * project's own id. Real gap found live: two local projects that happen to
 * share the same display name used to sync to Drive as two files with the
 * exact same name and no way to tell which was which just from the Drive
 * folder — the actual sync logic was always correct (matched by
 * `appProperties.evalhidProjectId`, see this file's own header, never by
 * name), this is purely a visual disambiguator. Deterministic — the SAME
 * project gets the SAME suffix on every sync, so this never creates a
 * second Drive file for a project that's already synced.
 */
function driveFileName(name: string, projectId: string): string {
  return `${sanitizedProjectFileName(name)} (${projectId.slice(0, 8)}).ehdz`;
}

export class DriveSyncStore {
  private accessToken: string | null = null;
  private folderId: string | null = null;
  /** Bumped by disconnect() — an async Drive operation (performSync, resolveConflict) captures this at the start and checks it again after its awaits, so it can tell whether the user disconnected while it was in flight and skip touching syncStates instead of resurrecting an entry in what's now supposed to be a cleared map. See disconnect()'s own comment. */
  private connectionEpoch = 0;
  private connecting = false;
  private lastError: unknown = null;
  private listeners = new Set<() => void>();
  private snapshot: DriveSyncSnapshot = {
    connected: false,
    connecting: false,
    lastError: null,
    needsReconnect: false,
  };
  private syncStates = new Map<string, ProjectSyncState>();
  /** projectIds with a performSync() currently in flight — see queueSync's own comment. */
  private inFlightSync = new Set<string>();
  /** The latest (meta, data) queueSync() got for a project WHILE it was already syncing — at most one entry per project, always the most recent (never a real queue of several). */
  private pendingResync = new Map<string, { meta: ProjectMeta; data: ProjectData }>();
  /** Counter, not a boolean — see pauseSync's own comment. */
  private syncPauseCount = 0;
  /** The latest (meta, data) queueSync() got WHILE paused — this app only ever has one project open at a time, so a single slot (not per-project, unlike pendingResync above) is enough. */
  private pendingWhilePaused: { meta: ProjectMeta; data: ProjectData } | null = null;

  /** Defaults to the real build-time env var; overridable so tests don't depend on VITE_GOOGLE_CLIENT_ID being set. */
  constructor(private clientId: string | null = CLIENT_ID) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): DriveSyncSnapshot => this.snapshot;

  isConnected(): boolean {
    return this.accessToken !== null;
  }

  getFolderId(): string | null {
    return this.folderId;
  }

  getProjectSyncState(projectId: string): ProjectSyncState {
    return this.syncStates.get(projectId) ?? NOT_SYNCED;
  }

  /**
   * Uploads (or updates) `projectId`'s `.ehdz` in the app's Drive folder, in
   * the background — fire-and-forget from the caller's point of view (the
   * local `saveProject` that triggered this has already resolved). No-op if
   * Drive isn't connected, or for an ephemeral (example) project: those are
   * throwaway/demo data, purged locally on the next app load, never meant
   * to land in the user's own Drive.
   *
   * Also a no-op while the project's sync state is already `"conflict"`
   * (`reconcile` flags this when Drive has a meaningfully newer copy) — the
   * project stays openable/editable even in that state (Landing.tsx doesn't
   * block it), so without this guard, simply opening a conflicted project
   * and making one more edit would queue a sync that silently overwrites
   * Drive's newer copy with this now-diverged local one. Only
   * `resolveConflict()` (the user's own "use Drive version" choice) or a
   * fresh `reconcile()` may clear `"conflict"`.
   *
   * Never runs two `performSync()`s for the same project at once — real
   * incident (2026-08-25): a large MITECO+ROCIO import can trigger several
   * autosaves in quick succession (one per sub-basin's data arriving), and
   * `exportProject()` (called inside `performSync`) is already the most
   * expensive step in the whole app for a big project (serializing +
   * compressing every series) — overlapping two of those at once was
   * multiplying an already-large memory peak. If a project is already
   * syncing when this is called again, the new `(meta, data)` is remembered
   * instead of starting a second upload — `startSync`'s own completion
   * handler re-queues with whatever is the LATEST pending data once the
   * in-flight one finishes, so the project always ends up synced to its
   * most recent state, just not via two concurrent uploads.
   */
  queueSync(meta: ProjectMeta, data: ProjectData): void {
    if (meta.ephemeral || !this.accessToken || !this.folderId) return;
    if (this.syncStates.get(meta.id)?.status === "conflict") return;
    if (this.syncPauseCount > 0) {
      this.pendingWhilePaused = { meta, data };
      return;
    }
    if (this.inFlightSync.has(meta.id)) {
      this.pendingResync.set(meta.id, { meta, data });
      return;
    }
    this.startSync(meta, data);
  }

  /**
   * Pauses `queueSync()` — a call arriving while paused is remembered (only
   * the latest, same coalescing idea as `pendingResync` above) instead of
   * starting an upload, until `resumeSync()` re-enables it. A counter, not
   * a boolean, so two overlapping callers pausing at once can't have one's
   * `resumeSync()` re-enable syncing while the other still expects it
   * paused.
   *
   * Used by `aemetJobStore.ts`'s `startQueued()` (see App.tsx's wiring) so
   * a large MITECO+ROCIO import — which can trigger an autosave, and so a
   * `queueSync()` call, once per sub-basin as its data arrives — doesn't
   * run `exportProject()` (already the single most expensive step in the
   * app for a big project) once per sub-basin; just once, with the final
   * state, when the whole batch finishes.
   */
  pauseSync = (): void => {
    this.syncPauseCount++;
  };

  /** Pairs with `pauseSync()` — see its own comment. Once every pause is released, runs exactly one sync with whatever's the latest data a paused `queueSync()` call saw, if any. */
  resumeSync = (): void => {
    this.syncPauseCount = Math.max(0, this.syncPauseCount - 1);
    if (this.syncPauseCount > 0) return;
    if (this.pendingWhilePaused) {
      const { meta, data } = this.pendingWhilePaused;
      this.pendingWhilePaused = null;
      this.queueSync(meta, data);
    }
  };

  private startSync(meta: ProjectMeta, data: ProjectData): void {
    const token = this.accessToken;
    const folderId = this.folderId;
    if (!token || !folderId) return; // Disconnected between queueSync's own check and this call — see the finally() below, this can happen on a re-queued pending sync.
    const existingFileId = this.syncStates.get(meta.id)?.driveFileId ?? null;
    this.inFlightSync.add(meta.id);
    this.patchSyncState(meta.id, { status: "syncing" });
    this.performSync(token, folderId, meta, data, existingFileId)
      .catch(() => {
        // Already recorded into syncStates by performSync — nothing more to do for a fire-and-forget caller.
      })
      .finally(() => {
        this.inFlightSync.delete(meta.id);
        const pending = this.pendingResync.get(meta.id);
        if (pending) {
          this.pendingResync.delete(meta.id);
          this.queueSync(pending.meta, pending.data);
        }
      });
  }

  private async performSync(
    token: string,
    folderId: string,
    meta: ProjectMeta,
    data: ProjectData,
    existingFileId: string | null
  ): Promise<void> {
    const epoch = this.connectionEpoch;
    try {
      const blob = await exportProject(meta.name, data);
      const uploaded = await uploadProjectFile(
        token,
        folderId,
        driveFileName(meta.name, meta.id),
        blob,
        meta.id,
        meta.lastModified,
        existingFileId ?? undefined
      );
      // The user disconnected while this upload was in flight — see
      // disconnect()'s own comment. Don't resurrect a syncStates entry for a
      // project that's supposed to look freshly disconnected now.
      if (this.connectionEpoch !== epoch) return;
      this.patchSyncState(meta.id, {
        status: "synced",
        driveFileId: uploaded.id,
        lastSyncedAt: Date.now(),
        lastError: null,
      });
    } catch (err) {
      if (this.connectionEpoch !== epoch) throw err; // still rethrow for startSync's own .catch(); just skip the state write.
      this.patchSyncState(meta.id, { status: "error", lastError: err });
      throw err;
    }
  }

  /**
   * Removes `projectId`'s Drive copy, if any — called right after the local
   * project is deleted, so "delete" means delete in both places rather than
   * silently orphaning a copy in the user's Drive forever. Best-effort: if
   * the Drive call fails (offline, expired token...), the local project is
   * gone either way, and a stray Drive file isn't catastrophic — a future
   * reconcile pass can still clean it up.
   */
  deleteRemote(projectId: string): void {
    const state = this.syncStates.get(projectId);
    this.syncStates.delete(projectId);
    if (this.accessToken && state?.driveFileId) {
      deleteProjectFile(this.accessToken, state.driveFileId).catch(() => {
        // Best-effort — see doc comment above.
      });
    }
    this.notify();
  }

  private patchSyncState(projectId: string, patch: Partial<ProjectSyncState>): void {
    const current = this.syncStates.get(projectId) ?? NOT_SYNCED;
    this.syncStates.set(projectId, { ...current, ...patch });
    this.notify();
  }

  /**
   * Matches the Drive folder against `localProjects` once, right after
   * connecting — see the file header for the exact rules. Downloads happen
   * one at a time (real projects, not worth parallelizing for what's
   * normally a handful of files); a single download/parse failure is
   * logged and skipped rather than aborting the rest. A local-newer push
   * (see the file header) goes through `queueSync` itself, so it's the same
   * fire-and-forget upload as any other — `reconcile` only awaits loading
   * the project's data from IndexedDB, not the upload finishing.
   */
  async reconcile(localProjects: ProjectMeta[]): Promise<{ unsyncedLocal: ProjectMeta[] }> {
    if (!this.accessToken || !this.folderId) return { unsyncedLocal: [] };
    const token = this.accessToken;
    const folderId = this.folderId;

    const driveFiles = await listProjectFiles(token, folderId);
    const byProjectId = new Map(
      driveFiles.filter((f) => f.projectId != null).map((f) => [f.projectId!, f])
    );

    const unsyncedLocal: ProjectMeta[] = [];
    for (const meta of localProjects) {
      if (meta.ephemeral) continue;
      const driveFile = byProjectId.get(meta.id);
      if (!driveFile) {
        unsyncedLocal.push(meta);
        continue;
      }
      byProjectId.delete(meta.id);
      const driveIsNewer = driveFile.lastModified - meta.lastModified > CONFLICT_THRESHOLD_MS;
      const localIsNewer = meta.lastModified - driveFile.lastModified > CONFLICT_THRESHOLD_MS;
      if (localIsNewer) {
        // Push the newer local copy now, instead of marking "synced" with a
        // stale Drive file — driveFileId is recorded first so queueSync
        // updates the existing file rather than creating a duplicate.
        this.patchSyncState(meta.id, { driveFileId: driveFile.id });
        const data = await loadSavedProject(meta.id);
        if (data) this.queueSync(meta, data);
        continue;
      }
      const status: ProjectSyncStatus = driveIsNewer ? "conflict" : "synced";
      this.patchSyncState(meta.id, {
        status,
        driveFileId: driveFile.id,
        lastSyncedAt: Date.now(),
        lastError: null,
      });
    }

    // Whatever's left exists in Drive but not locally — pull it down.
    for (const driveFile of byProjectId.values()) {
      try {
        const blob = await downloadProjectFile(token, driveFile.id);
        const file = new File([blob], driveFile.name, { type: "application/gzip" });
        const { name, data } = await importProject(file);
        const meta: ProjectMeta = {
          id: data.id,
          name,
          lastModified: driveFile.lastModified || Date.now(),
          ephemeral: false,
        };
        await saveProject(meta, data);
        this.patchSyncState(meta.id, {
          status: "synced",
          driveFileId: driveFile.id,
          lastSyncedAt: Date.now(),
          lastError: null,
        });
      } catch (err) {
        console.error(`Could not download the Drive-only project "${driveFile.name}":`, err);
      }
    }

    return { unsyncedLocal };
  }

  /**
   * "Use the Drive version" for a project flagged `"conflict"` (Landing.tsx)
   * — re-downloads it and overwrites the local copy under the SAME id, same
   * as a Drive-only project in `reconcile`. No-op if `projectId` isn't
   * actually flagged as a conflict (nothing to resolve) or Drive isn't
   * connected.
   */
  async resolveConflict(projectId: string): Promise<void> {
    const state = this.syncStates.get(projectId);
    if (!this.accessToken || state?.status !== "conflict" || !state.driveFileId) return;
    const token = this.accessToken;
    const driveFileId = state.driveFileId;
    // Same gate `startSync` uses — without this, a queueSync() landing while
    // this download/import/save is in flight (e.g. reconcile() racing a
    // user-triggered "use Drive version" right after connecting) sees status
    // no longer "conflict" and inFlightSync doesn't know about this method
    // either, so it starts a real upload in parallel with this resolve —
    // real hole found by deep-review (2026-08-29), see the regression test.
    this.inFlightSync.add(projectId);
    this.patchSyncState(projectId, { status: "syncing" });
    const epoch = this.connectionEpoch;
    try {
      const blob = await downloadProjectFile(token, driveFileId);
      const file = new File([blob], "project.ehdz", { type: "application/gzip" });
      const { name, data } = await importProject(file);
      const meta = buildProjectMeta(projectId, name, false);
      await saveProject(meta, data);
      // Same disconnect-race guard as performSync — see disconnect()'s own
      // comment. The local save above still happened (harmless/idempotent,
      // same as any other saveProject), just don't touch syncStates for a
      // project that's supposed to look freshly disconnected now.
      if (this.connectionEpoch !== epoch) return;
      this.patchSyncState(projectId, {
        status: "synced",
        driveFileId,
        lastSyncedAt: Date.now(),
        lastError: null,
      });
    } catch (err) {
      if (this.connectionEpoch !== epoch) throw err;
      this.patchSyncState(projectId, { status: "error", lastError: err });
      throw err;
    } finally {
      // Same drain `startSync` does in its own finally — a queueSync() that
      // got deferred into pendingResync while this was in flight still needs
      // to run once the gate above is released.
      this.inFlightSync.delete(projectId);
      const pending = this.pendingResync.get(projectId);
      if (pending) {
        this.pendingResync.delete(projectId);
        this.queueSync(pending.meta, pending.data);
      }
    }
  }

  async connect(): Promise<void> {
    await this.authenticate("consent", { surfaceErrors: true });
  }

  /**
   * Reuses a still-valid cached token from an earlier session/page load —
   * entirely local, no Google involved at all, so it can never trigger a
   * popup or a network round-trip. This is why a reload doesn't need to
   * reconnect (silently or not) EVERY time: only once the cached token has
   * actually expired does startup need to fall back to
   * `trySilentReconnect`. Call this first; it returns whether it actually
   * reconnected. Deliberately does NOT clear a present-but-expired entry —
   * `trySilentReconnect` (called right after this, when it's already
   * returned `false`) still needs it as its own "was connected before"
   * signal; it gets overwritten with a fresh one on the next successful
   * (re)connect, or cleared for real by `disconnect()`.
   */
  tryReuseStoredToken(): boolean {
    const stored = readStoredConnection();
    if (!stored || stored.expiresAt - EXPIRY_SAFETY_MARGIN_MS <= Date.now()) return false;
    this.accessToken = stored.accessToken;
    this.folderId = stored.folderId;
    this.notify();
    return true;
  }

  /** Silent reconnect on app startup, once `tryReuseStoredToken` has already come back empty (no cached token, or a genuinely expired one) — never surfaces an error, just leaves the store disconnected on failure (expired grant, no active Google session, offline, a browser silently blocking the popup a "prompt: ''" attempt sometimes still needs...); the user can press "Connect" again. */
  async trySilentReconnect(): Promise<void> {
    if (!this.clientId || !readStoredConnection()) return;
    try {
      await this.authenticate("", { surfaceErrors: false });
    } catch {
      // Deliberately silent — see doc comment above.
    }
  }

  disconnect(): void {
    // Bumped BEFORE clearing anything else — any performSync()/
    // resolveConflict() already past this point in flight (captured the
    // token in its own closure before this ran) checks this counter after
    // its own awaits and bails out instead of writing into syncStates below,
    // which is about to look "freshly cleared" to the rest of the app. Real
    // hole found by deep-review (2026-08-29): without this, an in-flight
    // upload/resolve completing after disconnect() silently resurrected an
    // entry here, and could finish uploading with the just-revoked token
    // before the revoke actually propagates on Google's side.
    this.connectionEpoch++;
    if (this.accessToken) revokeAccessToken(this.accessToken);
    this.accessToken = null;
    this.folderId = null;
    this.lastError = null;
    this.syncStates.clear();
    saveStoredConnection(null);
    this.notify();
  }

  /**
   * `surfaceErrors: false` (only `trySilentReconnect` passes this) keeps
   * `lastError` untouched on failure — a silent reconnect attempt failing
   * is expected/routine (no session, expired grant, a blocked popup...),
   * not something to surface in Settings the moment the app loads. Without
   * this, a real bug: `authenticate` used to always record `lastError`
   * regardless of caller, so `trySilentReconnect`'s own catch-and-swallow
   * only stopped the exception from propagating — the store had already
   * notified subscribers with the error before that catch ever ran.
   */
  private async authenticate(
    prompt: "" | "consent",
    { surfaceErrors }: { surfaceErrors: boolean }
  ): Promise<void> {
    if (!this.clientId) {
      const err = new TranslatableError("driveSync.notConfigured");
      if (surfaceErrors) {
        this.lastError = err;
        this.notify();
      }
      throw err;
    }
    this.connecting = true;
    if (surfaceErrors) this.lastError = null;
    this.notify();
    try {
      const { accessToken, expiresAt } = await requestAccessToken(this.clientId, { prompt });
      const folderId = await findOrCreateAppFolder(accessToken);
      this.accessToken = accessToken;
      this.folderId = folderId;
      saveStoredConnection({ accessToken, folderId, expiresAt });
    } catch (err) {
      if (surfaceErrors) this.lastError = err;
      throw err;
    } finally {
      this.connecting = false;
      this.notify();
    }
  }

  private notify(): void {
    this.snapshot = {
      connected: this.accessToken !== null,
      connecting: this.connecting,
      lastError: this.lastError,
      needsReconnect:
        !this.connecting && this.accessToken === null && readStoredConnection() !== null,
    };
    this.listeners.forEach((l) => l());
  }
}

export const driveSyncStore = new DriveSyncStore();
